import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { FaThumbsUp, FaThumbsDown } from "react-icons/fa";
import { Search, Eye, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import {
  useGetFeedbackRequestQuery,
  useDeleteFeedbackRequestMutation,
} from "../slices/feedbackSlice";
import { useGetAcademyQuery } from "../slices/userApiSlice";
import CustomSelect from "../components/CustomSelect/CustomSelect";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const FeedbackReport = () => {
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  const [search, setSearch] = useState("");
  const [selectedAcademyId, setSelectedAcademyId] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const {
    data: feedbackData,
    isLoading: isFeedbackLoading,
    refetch,
  } = useGetFeedbackRequestQuery();
  const [deleteFeedback] = useDeleteFeedbackRequestMutation();
  const { data: academies } = useGetAcademyQuery();

  // Helper function to get academy name by ID
  const getAcademyName = (academyId) => {
    if (academyId === "all" || !academyId) return "Academy";
    const academy = academies?.find(
      (a) => (a._id || a.id || a.academyId) === academyId,
    );
    return academy?.name || "Academy";
  };

  // Prepare academy options for CustomSelect
  const academyOptions = useMemo(() => {
    if (!academies || !Array.isArray(academies)) return [];
    return academies.map((academy) => ({
      value: academy._id || academy.id || academy.academyId,
      label: academy.name,
      ...academy,
    }));
  }, [academies]);

  // Helper to extract academy id and name from a feedback item
  const getItemAcademy = (item) => {
    // item.academy can be an object (populated) or a string id/name
    if (!item || !item.academy) return { id: null, name: null };
    if (typeof item.academy === "object") {
      return {
        id:
          item.academy._id || item.academy.id || item.academy.academyId || null,
        name: item.academy.name || null,
      };
    }
    // string: could be id or name
    return { id: item.academy, name: null };
  };

  const getAcademyDisplayName = (item) => {
    const { id, name } = getItemAcademy(item);
    if (name) return name;
    if (!id) return "Unknown Academy";

    const matched = academies?.find((a) => {
      const academyId = a._id || a.id || a.academyId;
      return academyId === id;
    });

    return matched?.name || "Unknown Academy";
  };

  const resolveSubmissionDate = (item) => {
    if (!item) return null;
    const candidate =
      item?.createdAt ||
      item?.created_at ||
      item?.submittedAt ||
      item?.submitted_at ||
      item?.date ||
      item?.feedbackDate ||
      item?.timestamp ||
      item?.createdOn ||
      item?.created_on ||
      null;

    return parseTimestamp(candidate) || deriveFromObjectId(item);
  };

  const formatSubmissionTimestamp = (item) => {
    const parsed = resolveSubmissionDate(item);
    if (!parsed) return "Unknown";

    return parsed.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const parseTimestamp = (raw) => {
    if (raw == null || raw === "") return null;

    if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;

    if (typeof raw === "number") {
      return new Date(raw < 10_000_000_000 ? raw * 1000 : raw);
    }

    if (typeof raw === "string") {
      const trimmed = raw.trim();
      if (!trimmed) return null;

      if (/^-?\d+$/.test(trimmed)) {
        const num = Number(trimmed);
        return new Date(trimmed.length === 10 ? num * 1000 : num);
      }

      const normalized = trimmed.replace(/,/g, "");
      const direct = new Date(normalized);
      if (!Number.isNaN(direct.getTime())) return direct;

      const dayFirstMatch = normalized.match(
        /^([0-3]?\d)[-/]([0-3]?\d)[-/](\d{2,4})(?:\s+([0-2]?\d)(?::([0-5]?\d))?(?::([0-5]?\d))?\s*(AM|PM)?)?$/i,
      );
      if (dayFirstMatch) {
        const [, dd, mm, yyyyRaw, hhRaw, minRaw, secRaw, meridian] =
          dayFirstMatch;
        const year = Number(yyyyRaw.length === 2 ? `20${yyyyRaw}` : yyyyRaw);
        let hours = Number(hhRaw ?? "0");
        if (meridian) {
          const upper = meridian.toUpperCase();
          if (upper === "PM" && hours < 12) hours += 12;
          if (upper === "AM" && hours === 12) hours = 0;
        }
        const minutes = Number(minRaw ?? "0");
        const seconds = Number(secRaw ?? "0");
        return new Date(
          year,
          Number(mm) - 1,
          Number(dd),
          hours,
          minutes,
          seconds,
        );
      }

      return null;
    }

    if (typeof raw === "object") {
      if (typeof raw.toDate === "function") {
        const fromMethod = raw.toDate();
        return fromMethod instanceof Date ? fromMethod : null;
      }

      if (raw.$date != null) return parseTimestamp(raw.$date);

      if (raw.$numberLong != null)
        return parseTimestamp(Number(raw.$numberLong));

      if (raw.seconds != null) {
        const ms = raw.seconds * 1000 + (raw.nanoseconds || 0) / 1_000_000;
        return new Date(ms);
      }

      if (raw._seconds != null) {
        const ms = raw._seconds * 1000 + (raw._nanoseconds || 0) / 1_000_000;
        return new Date(ms);
      }
    }

    return null;
  };

  const deriveFromObjectId = (item) => {
    const source = item?._id || item?.id;
    if (typeof source !== "string" || source.length < 8) return null;
    const hex = source.slice(0, 8);
    if (!/^[0-9a-fA-F]+$/.test(hex)) return null;
    const seconds = Number.parseInt(hex, 16);
    if (Number.isNaN(seconds)) return null;
    return new Date(seconds * 1000);
  };

  // Filtered Feedback sorted newest first
  const filteredFeedback = Array.isArray(feedbackData)
    ? feedbackData
        .filter((item) => {
          const title = (item.title || item.subject || "").toLowerCase();
          const matchesSearch = title.includes(search.toLowerCase());

          if (selectedAcademyId === "all" || !selectedAcademyId)
            return matchesSearch;

          const itemAcademy = getItemAcademy(item);
          const idMatch =
            itemAcademy.id &&
            selectedAcademyId &&
            itemAcademy.id === selectedAcademyId;

          return matchesSearch && idMatch;
        })
        .sort((a, b) => {
          const aTime = resolveSubmissionDate(a)?.getTime() ?? 0;
          const bTime = resolveSubmissionDate(b)?.getTime() ?? 0;
          return bTime - aTime;
        })
    : [];

  const totalPages = Math.ceil(filteredFeedback.length / rowsPerPage);
  const displayedData = filteredFeedback.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const handleDelete = async (feedbackId) => {
    if (!feedbackId) return;
    setDeleteConfirm({ open: true, id: feedbackId });
  };

  const confirmDelete = async () => {
    try {
      await deleteFeedback(deleteConfirm.id).unwrap();
      toast.success("Feedback deleted successfully");
      refetch();
      setDeleteConfirm({ open: false, id: null });
    } catch (error) {
      toast.error("Failed to delete feedback", {
        description: error?.data?.message || "Please try again.",
      });
    }
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card p-6">
        <h1 className="text-2xl font-semibold">Feedback & Reviews</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Total: {filteredFeedback.length} feedback
          {filteredFeedback.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Controls */}
      <div className="border-b bg-card p-4 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search feedback by title..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <CustomSelect
          options={academyOptions}
          value={selectedAcademyId}
          onChange={setSelectedAcademyId}
          placeholder="Academy"
          allOptionLabel="All Academies"
          getDisplayValue={getAcademyName}
          allValue="all"
          minWidth={180}
        />
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-24">Type</TableHead>
                <TableHead className="w-48">Title</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-40">Academy</TableHead>
                <TableHead className="w-44">Submitted</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isFeedbackLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center text-muted-foreground h-32"
                  >
                    Loading feedback...
                  </TableCell>
                </TableRow>
              ) : displayedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center text-muted-foreground h-32"
                  >
                    No feedback found
                  </TableCell>
                </TableRow>
              ) : (
                displayedData.map((item) => {
                  const feedbackId = item._id || item.id;
                  const title = item.title || item.subject || "Untitled";
                  const description =
                    item.description || item.feedback || "No description";
                  const isPositive =
                    item.liked ||
                    item.type === "positive" ||
                    item.reviewType === "positive";
                  const academyName = getAcademyDisplayName(item);
                  const timestamp = formatSubmissionTimestamp(item);

                  return (
                    <TableRow key={feedbackId}>
                      <TableCell>
                        <Badge variant={isPositive ? "success" : "destructive"}>
                          {isPositive ? "Positive" : "Negative"}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{title}</TableCell>
                      <TableCell className="max-w-md truncate">
                        {description}
                      </TableCell>
                      <TableCell>{academyName}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {timestamp}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedFeedback(item)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(feedbackId)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {filteredFeedback.length > 0 && (
        <div className="border-t bg-card p-4 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
            {Math.min(currentPage * rowsPerPage, filteredFeedback.length)} of{" "}
            {filteredFeedback.length} results
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronLeft className="h-4 w-4 ml-1 rotate-180" />
            </Button>
          </div>
        </div>
      )}

      {/* Details Dialog */}
      {selectedFeedback && (
        <Dialog
          open={!!selectedFeedback}
          onOpenChange={() => setSelectedFeedback(null)}
        >
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Feedback Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <p className="text-sm mt-1">
                  {selectedFeedback.title ||
                    selectedFeedback.subject ||
                    "Untitled"}
                </p>
              </div>
              <div>
                <Label>Type</Label>
                <div className="mt-1">
                  <Badge
                    variant={
                      selectedFeedback.liked ||
                      selectedFeedback.type === "positive" ||
                      selectedFeedback.reviewType === "positive"
                        ? "success"
                        : "destructive"
                    }
                  >
                    {selectedFeedback.liked ||
                    selectedFeedback.type === "positive" ||
                    selectedFeedback.reviewType === "positive"
                      ? "Positive"
                      : "Negative"}
                  </Badge>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={
                    selectedFeedback.description ||
                    selectedFeedback.feedback ||
                    "No description"
                  }
                  readOnly
                  className="mt-1 min-h-32"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Academy</Label>
                  <p className="text-sm mt-1">
                    {getAcademyDisplayName(selectedFeedback)}
                  </p>
                </div>
                <div>
                  <Label>Submitted</Label>
                  <p className="text-sm mt-1">
                    {formatSubmissionTimestamp(selectedFeedback)}
                  </p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteConfirm.open}
        onOpenChange={(open) =>
          !open && setDeleteConfirm({ open: false, id: null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this feedback. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setDeleteConfirm({ open: false, id: null })}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FeedbackReport;
